// client/src/components/plataforma/users/tabs/PermissoesUsuarioTab.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Shield, MapPin, Save, Loader2, AlertCircle, XCircle, Info, Briefcase, Star, Calendar, Settings } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../../../services/api';
import { useAuth } from '../../../../context/auth/AuthProvider';
import { useUnidadesPorConta } from '../../../../hooks/useUnidades';
import { useRoles } from '../../../../hooks/useRoles';
import { useRelationshipsMutations, useUserFranquiasByUser, ACCOUNT_USERS_QUERY_KEY } from '../../../../hooks/useRelationships';
import useAgendas from '../../../../hooks/useAgendas';
import useAlert from '../../../../hooks/useAlert';

const PermissoesUsuarioTab = ({ user, contextData, onUpdateSuccess, onChange, isCreating }) => {
    const { user: currentUser } = useAuth();
    const { setAlert } = useAlert();
    const queryClient = useQueryClient();
    
    const targetUserId = user?._id || user?.id;
    
    // Controle interno de edição para exibir os botões nativos do Header
    const [localIsEditing, setLocalIsEditing] = useState(isCreating);
    const [isSaving, setIsSaving] = useState(false);
    
    const [selectedUnidades, setSelectedUnidades] = useState([]); 
    const [unidadePrincipalId, setUnidadePrincipalId] = useState('');
    const [rolePrincipalId, setRolePrincipalId] = useState('');
    const [statusVinculo, setStatusVinculo] = useState('ativo'); // ✅ ADICIONADO ESTADO DO STATUS
    const [accountUserLink, setAccountUserLink] = useState(null);

    const [agendasAcesso, setAgendasAcesso] = useState({});

    const contaId = contextData?.contaId;
    const franquiaId = contextData?.franquiaId;
    // Modo franquia: editando um admin de franquia (/franquias/:id/admins/...).
    // Aqui não existe conta, unidade nem agenda — só o Papel (Role) do vínculo
    // UserFranquia. As seções de unidades/agendas ficam ocultas.
    const isFranquiaMode = !!franquiaId && !contaId;
    const isOwnUser = currentUser?._id === targetUserId;

    const { data: unidadesData, isLoading: loadingUnidades } = useUnidadesPorConta(contaId, {
        enabled: !!contaId, ativo: true
    });
    const unidades = unidadesData?.data || [];

    const { data: rolesData, isLoading: loadingRoles } = useRoles({
        account: contaId,
        franquia: franquiaId,
        scope: isFranquiaMode ? 'franquia' : undefined
    });

    // Vínculo UserFranquia (só no modo franquia) — traz a role atual do admin.
    const { data: userFranquiasData, isLoading: loadingUserFranquias } = useUserFranquiasByUser(
        isFranquiaMode ? targetUserId : null,
        { enabled: isFranquiaMode && !!targetUserId && !isCreating }
    );
    const franquiaVinculo = useMemo(() => {
        const lista = userFranquiasData?.data || (Array.isArray(userFranquiasData) ? userFranquiasData : []);
        if (!lista.length) return null;
        // A resposta de /relationships/users/:id/franquias vem "achatada":
        // o _id do item É o da franquia; o vínculo fica em relationshipId.
        const match = lista.find(v => String(v._id || v.franquia?._id || v.franquia) === String(franquiaId));
        return match || lista[0];
    }, [userFranquiasData, franquiaId]);

    const { useAgendasQuery, updateAgendaPermissions } = useAgendas();
    const { data: agendasData, isLoading: loadingAgendas } = useAgendasQuery({ account: contaId, limit: 100 }, { enabled: !!contaId && !isCreating });
    const agendasList = Array.isArray(agendasData) ? agendasData : (agendasData?.data || []);

    const rolesDisponiveis = useMemo(() => {
        if (!rolesData) return [];
        let rolesArray = Array.isArray(rolesData) ? rolesData : (rolesData.data || []);

        let rolesFiltradas = rolesArray;

        // No modo franquia só faz sentido oferecer papéis de escopo franquia.
        if (isFranquiaMode) {
            rolesFiltradas = rolesFiltradas.filter(role => role.scope === 'franquia');
        } else {
            // Limita pelo escopo de quem está editando (usa role.scope — o
            // role.name no banco é rótulo, "Administrador da Conta", nunca
            // "conta_admin", então comparar por name sempre falhava).
            const escopo = currentUser?.role?.scope;
            if (escopo === 'conta') {
                rolesFiltradas = rolesFiltradas.filter(role => role.scope === 'conta' || role.scope === 'cliente');
            } else if (escopo === 'franquia') {
                rolesFiltradas = rolesFiltradas.filter(role => ['franquia', 'conta', 'cliente'].includes(role.scope));
            }
        }

        return rolesFiltradas
            .filter(role => role && (role._id || role.id) && role.name)
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [rolesData, currentUser, isFranquiaMode]);

    const extractId = useCallback((field) => {
        if (!field) return '';
        if (typeof field === 'string') return field;
        if (typeof field === 'object') {
            if (field.$oid) return String(field.$oid);
            if (field._id) {
                if (typeof field._id === 'object' && field._id.$oid) return String(field._id.$oid);
                return String(field._id);
            }
            if (field.id) return String(field.id);
            return String(field); 
        }
        return String(field);
    }, []);

    const { data: vinculoData, isLoading: loadingVinculo, refetch: refetchVinculo } = useQuery({
        queryKey: ['accountUser-direct-permissions', targetUserId, contaId],
        queryFn: async () => {
            if (!targetUserId || !contaId || isCreating) return null;
            
            try {
                const response = await api.get('/relationships/account-users', { 
                    params: { user: targetUserId, account: contaId } 
                });
                
                const data = response.data?.data;
                if (data && data.length > 0) {
                    return data[0]; 
                }
                return null;
            } catch (error) {
                console.error("Erro ao buscar vínculo do usuário na conta:", error);
                return null;
            }
        },
        enabled: !!targetUserId && !!contaId && !isCreating,
        staleTime: 0,
    });

    useEffect(() => {
        if (vinculoData && !isCreating) {
            setAccountUserLink(vinculoData);
            
            const strUnidadePrincipal = extractId(vinculoData.unidadePrincipal);
            const strRolePrincipal = extractId(vinculoData.role);

            setUnidadePrincipalId(strUnidadePrincipal);
            setRolePrincipalId(strRolePrincipal);
            setStatusVinculo(vinculoData.status || 'ativo'); // ✅ INICIALIZA O STATUS DO BANCO

            if (vinculoData.unidades && Array.isArray(vinculoData.unidades)) {
                const arrUnidadesFormatadas = vinculoData.unidades.map(u => {
                    if (typeof u === 'string') return { unidade: u, role: null };
                    if (typeof u === 'object' && u !== null) {
                        return { 
                            unidade: extractId(u.unidade || u), 
                            role: extractId(u.role) || null 
                        };
                    }
                    return { unidade: String(u), role: null };
                }).filter(item => item.unidade && item.unidade !== 'undefined');
                
                setSelectedUnidades(arrUnidadesFormatadas);
            } else {
                setSelectedUnidades([]);
            }
        }
    }, [vinculoData, isCreating, extractId]);

    // Modo franquia: a role vem do vínculo UserFranquia; se ainda não
    // carregou, cai pra user.role (já resolvido no UserManagementDrawer).
    useEffect(() => {
        if (!isFranquiaMode || isCreating) return;
        const roleFonte = franquiaVinculo?.role ?? user?.role;
        const strRole = extractId(roleFonte);
        if (strRole) setRolePrincipalId(strRole);
        if (franquiaVinculo) {
            setAccountUserLink(franquiaVinculo);
            setStatusVinculo(franquiaVinculo.status || 'ativo');
        }
    }, [isFranquiaMode, isCreating, franquiaVinculo, user, extractId]);

    useEffect(() => {
        if (isCreating && user) {
            setRolePrincipalId(extractId(user.role));
            setUnidadePrincipalId(extractId(user.unidadePrincipal));
            setStatusVinculo(user.status || 'ativo'); // ✅ INICIALIZA O STATUS NO CREATING
            
            if (user.unidades && Array.isArray(user.unidades)) {
                const arr = user.unidades.map(u => {
                    if (typeof u === 'string') return { unidade: String(u), role: null };
                    if (typeof u === 'object' && u !== null) {
                        return { 
                            unidade: extractId(u.unidade || u._id || u.id), 
                            role: extractId(u.role) || null 
                        };
                    }
                    return { unidade: String(u), role: null };
                }).filter(item => item.unidade);
                setSelectedUnidades(arr);
            }
        }
    }, [isCreating, user, extractId]);

    useEffect(() => {
        if (agendasList && agendasList.length > 0 && targetUserId && !isCreating) {
            const controleLocal = {};
            agendasList.forEach(agenda => {
                const isViewer = agenda.permissoes?.visualizadores?.some(v => extractId(v) === targetUserId);
                const isEditor = agenda.permissoes?.editores?.some(v => extractId(v) === targetUserId);
                controleLocal[agenda._id] = { 
                    visivel: isViewer || isEditor, 
                    editor: isEditor 
                };
            });
            setAgendasAcesso(controleLocal);
        }
    }, [agendasList, targetUserId, isCreating, extractId]);

    const { updateAccountUser, createAccountUser, invalidateRelationships } = useRelationshipsMutations();

    const handleRolePrincipalChange = (e) => {
        const val = e.target.value;
        setRolePrincipalId(val);
        if (onChange) onChange({ target: { name: 'role', value: val } });
    };

    const handleUnidadePrincipalChange = (e) => {
        const val = e.target.value;
        setUnidadePrincipalId(val);
        if (onChange) onChange({ target: { name: 'unidadePrincipal', value: val } });
    };

    const handleToggleUnidade = (unidadeId) => {
        const strId = String(unidadeId);
        const exists = selectedUnidades.find(item => item.unidade === strId);
        
        let newSelection;
        if (exists) {
            newSelection = selectedUnidades.filter(item => item.unidade !== strId);
        } else {
            newSelection = [...selectedUnidades, { unidade: strId, role: null }];
        }
        
        setSelectedUnidades(newSelection);
        if (onChange) onChange({ target: { name: 'unidades', value: newSelection } });
    };

    const handleRoleUnidadeChange = (unidadeId, roleId) => {
        const strId = String(unidadeId);
        const newSelection = selectedUnidades.map(item => {
            if (item.unidade === strId) return { ...item, role: roleId || null };
            return item;
        });
        
        setSelectedUnidades(newSelection);
        if (onChange) onChange({ target: { name: 'unidades', value: newSelection } });
    };

    const handleSelectAll = () => {
        const allIds = unidades.map(u => ({ unidade: String(u._id || u.id), role: null }));
        setSelectedUnidades(allIds);
        if (onChange) onChange({ target: { name: 'unidades', value: allIds } });
    };

    const handleClear = () => {
        setSelectedUnidades([]);
        if (onChange) onChange({ target: { name: 'unidades', value: [] } });
    };

    const handleAgendaToggle = (agendaId, tipo) => {
        setAgendasAcesso(prev => {
            const atual = prev[agendaId] || { visivel: false, editor: false };
            const novo = { ...atual };

            if (tipo === 'visivel') {
                novo.visivel = !atual.visivel;
                if (!novo.visivel) novo.editor = false; // Se tira visibilidade, tira edição
            } else if (tipo === 'editor') {
                novo.editor = !atual.editor;
                if (novo.editor) novo.visivel = true; // Se é editor, deve ser visível
            }

            return { ...prev, [agendaId]: novo };
        });
    };

    const handleSave = async () => {
        if (isCreating) return;
        // Modo franquia não tem save próprio — a role é persistida pelo
        // botão "Salvar Alterações" do drawer (via onChange → formData.role).
        if (isFranquiaMode) return;

        if (!targetUserId || !contaId || !rolePrincipalId) {
            setAlert('O Papel (Role) principal é obrigatório', 'warning');
            return;
        }

        setIsSaving(true);

        try {
            const vinculoId = accountUserLink?._id || user.relationshipId;
            
            const unidadesFormatadas = selectedUnidades.map(item => ({
                unidade: item.unidade,
                role: item.role || rolePrincipalId
            }));

            // ✅ STATUS INJETADO NO PAYLOAD
            const payload = {
                role: rolePrincipalId,
                unidadePrincipal: unidadePrincipalId || null,
                unidades: unidadesFormatadas,
                status: statusVinculo 
            };

            if (vinculoId) {
                await updateAccountUser.mutateAsync({ id: vinculoId, data: payload });
            } else {
                await createAccountUser.mutateAsync({
                    account: contaId,
                    user: targetUserId,
                    criadoPor: currentUser?._id,
                    ...payload
                });
            }

            if (agendasList && agendasList.length > 0) {
                for (const agenda of agendasList) {
                    const cfg = agendasAcesso[agenda._id] || { visivel: false, editor: false };
                    
                    const visViewerIds = agenda.permissoes?.visualizadores?.map(extractId).filter(id => id !== targetUserId) || [];
                    const visEditorIds = agenda.permissoes?.editores?.map(extractId).filter(id => id !== targetUserId) || [];

                    if (cfg.visivel) visViewerIds.push(targetUserId);
                    if (cfg.editor) visEditorIds.push(targetUserId);

                    const novasPermissoes = {
                        ...agenda.permissoes,
                        visualizadores: visViewerIds,
                        editores: visEditorIds
                    };

                    await updateAgendaPermissions.mutateAsync({
                        agendaId: agenda._id,
                        permissoesData: novasPermissoes
                    });
                }
            }

            invalidateRelationships();
            queryClient.invalidateQueries({ queryKey: [ACCOUNT_USERS_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: ['accountUser-direct-permissions'] });
            
            await refetchVinculo();
            
            setAlert('Permissões e Acessos atualizados com sucesso!', 'success');
            setLocalIsEditing(false);
            
            if (onUpdateSuccess) {
                onUpdateSuccess();
            }
        } catch (error) {
            console.error('❌ Erro ao salvar permissões:', error);
            setAlert(error.message || 'Erro ao salvar permissões', 'danger');
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = useCallback(() => {
        setLocalIsEditing(false);
        
        if (accountUserLink) {
            setUnidadePrincipalId(extractId(accountUserLink.unidadePrincipal));
            setRolePrincipalId(extractId(accountUserLink.role));
            setStatusVinculo(accountUserLink.status || 'ativo'); // ✅ RESTAURA O STATUS

            if (accountUserLink.unidades && Array.isArray(accountUserLink.unidades)) {
                const arr = accountUserLink.unidades.map(u => {
                    if (typeof u === 'string') return { unidade: u, role: null };
                    if (typeof u === 'object' && u !== null) {
                        return { 
                            unidade: extractId(u.unidade || u), 
                            role: extractId(u.role) || null 
                        };
                    }
                    return { unidade: String(u), role: null };
                }).filter(item => item.unidade);
                setSelectedUnidades(arr);
            } else {
                setSelectedUnidades([]);
            }
        }
    }, [accountUserLink, extractId]);

    if (loadingUnidades || loadingVinculo || loadingRoles || loadingAgendas || loadingUserFranquias) {
        return (
            <div className="flex justify-center items-center py-12">
                <Loader2 size={32} className="animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="form-content space-y-6">
            
            {/* ✅ CABEÇALHO PADRONIZADO COM BOTÕES DE AÇÃO */}
            <div className="form-header flex items-center justify-between mb-4">
                <div>
                    <h3 className="form-title flex items-center mb-0">
                        <Shield size={20} className="mr-2 text-primary" />
                        Permissões de Acesso
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                        {isFranquiaMode
                            ? 'Papel deste administrador na franquia.'
                            : 'Gerencie o acesso do usuário à conta e às unidades.'}
                    </p>
                </div>

                {!isCreating && !isFranquiaMode && (
                    !localIsEditing ? (
                        <button 
                            type="button" 
                            onClick={() => setLocalIsEditing(true)} 
                            className="action-button action-button-secondary bg-white border border-gray-300 shadow-sm text-gray-700"
                        >
                            <Settings size={16} className="mr-2 text-gray-500" /> Editar Permissões
                        </button>
                    ) : (
                        <div className="flex gap-2">
                            <button 
                                type="button" 
                                onClick={handleCancel} 
                                className="action-button action-button-secondary bg-white border border-gray-300 shadow-sm text-gray-700"
                            >
                                <XCircle size={16} className="mr-2 text-gray-500" /> Cancelar
                            </button>
                            <button 
                                type="button" 
                                onClick={handleSave} 
                                disabled={isSaving} 
                                className="action-button action-button-primary bg-primary text-white shadow-sm"
                            >
                                {isSaving ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
                                Salvar Permissões
                            </button>
                        </div>
                    )
                )}
            </div>

            {isOwnUser && localIsEditing && !isCreating && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                        <AlertCircle className="text-orange-600 mt-0.5" size={20} />
                        <div>
                            <h4 className="font-medium text-orange-800">Editando suas próprias permissões</h4>
                            <p className="text-sm text-orange-700 mt-1">
                                Tenha cuidado. Alterar sua própria <strong>Role Principal</strong> ou remover unidades pode fazer você perder acesso ao sistema imediatamente.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* SEÇÃO GLOBAL DA CONTA / PAPEL NA FRANQUIA */}
            <fieldset className="form-section">
                <legend className="form-section-title">
                    <Briefcase size={16} className="inline mr-2" />
                    {isFranquiaMode ? 'Papel na Franquia' : 'Acesso Global da Conta'}
                </legend>

                <div className="form-grid-container">
                    <div className="form-group">
                        <label className="required">Papel Principal (Role)</label>
                        <select
                            value={rolePrincipalId}
                            onChange={handleRolePrincipalChange}
                            className={`form-input ${(!localIsEditing || isFranquiaMode) ? 'bg-gray-50 text-gray-600' : 'bg-white'}`}
                            disabled={isFranquiaMode || !localIsEditing || (isOwnUser && !isCreating)}
                        >
                            <option value="">Selecione uma role...</option>
                            {rolesDisponiveis.map(role => (
                                <option key={`mainrole-${role._id || role.id}`} value={String(role._id || role.id)}>
                                    {role.name} {role.scope ? `[${role.scope}]` : ''}
                                </option>
                            ))}
                        </select>
                        <p className="form-hint mt-1">
                            {isFranquiaMode
                                ? 'Papel deste usuário como administrador da franquia. Para trocar de papel ou remover o acesso, use a lista "Admins da Franquia".'
                                : 'Este papel será aplicado em todas as unidades caso não seja especificado outro.'}
                        </p>
                    </div>

                    {!isFranquiaMode && (
                    <>
                    <div className="form-group">
                        <label>Unidade Principal</label>
                        <select
                            value={unidadePrincipalId}
                            onChange={handleUnidadePrincipalChange}
                            className={`form-input ${!localIsEditing ? 'bg-gray-50 text-gray-600' : 'bg-white'}`}
                            disabled={!localIsEditing}
                        >
                            <option value="">Nenhuma (Unidade Padrão)</option>
                            {unidades.map(unidade => (
                                <option key={`mainunit-${unidade._id || unidade.id}`} value={String(unidade._id || unidade.id)}>
                                    {unidade.nome} {unidade.localidade ? `(${unidade.localidade})` : ''}
                                </option>
                            ))}
                        </select>
                        <p className="form-hint mt-1">Sua unidade padrão ao entrar no sistema.</p>
                    </div>

                    {/* ✅ STATUS MOVIDO PARA CÁ COM NOME GENÉRICO */}
                    <div className="form-group md:col-span-2 pt-4 border-t border-gray-100">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={statusVinculo === 'ativo'}
                                onChange={(e) => {
                                    const newStatus = e.target.checked ? 'ativo' : 'inativo';
                                    setStatusVinculo(newStatus);
                                    if (onChange) {
                                        onChange({ target: { name: 'status', value: newStatus } });
                                        // Opcionalmente atualiza o isActive global na criação
                                        if (isCreating) onChange({ target: { name: 'isActive', value: e.target.checked, type: 'checkbox', checked: e.target.checked } });
                                    }
                                }}
                                className="form-checkbox"
                                disabled={!localIsEditing || (isOwnUser && !isCreating)}
                            />
                            <span className="checkbox-label-text font-medium text-gray-700">
                                Usuário ativo
                            </span>
                        </label>
                        <p className="form-hint mt-1 ml-6">
                            Desmarque para bloquear o acesso deste usuário ao sistema desta clínica.
                        </p>
                    </div>
                    </>
                    )}
                </div>
            </fieldset>

            {/* SEÇÃO UNIDADES ESPECÍFICAS */}
            {!isFranquiaMode && (
            <fieldset className="form-section">
                <legend className="form-section-title">
                    <MapPin size={16} className="inline mr-2" />
                    Unidades de Acesso
                    <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full font-normal">
                        {selectedUnidades.length} selecionada(s)
                    </span>
                    {localIsEditing && (
                        <div className="float-right flex gap-3 -mt-1">
                            <button type="button" onClick={handleSelectAll} className="text-xs text-primary hover:underline font-medium">Selecionar Todas</button>
                            <button type="button" onClick={handleClear} className="text-xs text-gray-500 hover:underline">Limpar</button>
                        </div>
                    )}
                </legend>

                {unidades.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                        <MapPin size={32} className="mx-auto text-gray-400 mb-2" />
                        <p className="text-gray-500">Nenhuma unidade cadastrada nesta conta.</p>
                    </div>
                ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto pr-2 mt-4">
                        {unidades.map(unidade => {
                            const idStr = String(unidade._id || unidade.id);
                            const selection = selectedUnidades.find(item => item.unidade === idStr);
                            const isSelected = !!selection;
                            const isPrincipal = idStr === unidadePrincipalId;

                            return (
                                <div 
                                    key={`unidade-${idStr}`} 
                                    className={`flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 border rounded-lg transition-colors ${
                                        isSelected ? 'border-primary bg-blue-50/10' : 'border-gray-200 hover:bg-gray-50'
                                    } ${!localIsEditing ? 'opacity-90' : ''}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => handleToggleUnidade(idStr)}
                                            className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer disabled:cursor-not-allowed"
                                            disabled={!localIsEditing}
                                        />
                                        <div>
                                            <div className={`font-medium flex items-center gap-2 ${isSelected ? 'text-primary' : 'text-gray-900'}`}>
                                                {unidade.nome}
                                                {isPrincipal && (
                                                    <span className="inline-flex items-center text-[10px] bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded" title="Unidade Principal">
                                                        <Star size={10} className="mr-1" /> Principal
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-gray-500 mt-0.5">
                                                {unidade.endereco?.localidade} {unidade.endereco?.uf ? `- ${unidade.endereco?.uf}` : ''}
                                            </div>
                                        </div>
                                    </div>

                                    {isSelected && (
                                        <div className="w-full sm:w-[250px] mt-2 sm:mt-0">
                                            <select
                                                value={selection.role || ''}
                                                onChange={(e) => handleRoleUnidadeChange(idStr, e.target.value)}
                                                className={`form-input w-full text-sm py-2 px-3 h-10 ${!localIsEditing ? 'bg-gray-50 text-gray-600' : 'bg-white'}`}
                                                disabled={!localIsEditing}
                                            >
                                                <option value="">Personalizado: (Herdar Principal)</option>
                                                {rolesDisponiveis.map(role => (
                                                    <option key={`subrole-${role._id || role.id}`} value={String(role._id || role.id)}>
                                                        Personalizado: {role.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mt-4">
                    <div className="flex items-start gap-2">
                        <Info size={16} className="text-gray-500 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-gray-600">
                            <strong>Dica:</strong> Você pode definir que um usuário é "Gerente" globalmente (Role Principal), mas configurá-lo apenas como "Profissional" em uma Unidade Específica. Se você deixar como "(Herdar Principal)", ele usará a permissão global.
                        </p>
                    </div>
                </div>
            </fieldset>
            )}

            {/* SEÇÃO AGENDAS */}
            {!isFranquiaMode && !isCreating && agendasList && agendasList.length > 0 && (
                <fieldset className="form-section">
                    <legend className="form-section-title">
                        <Calendar size={16} className="inline mr-2" />
                        Acesso a Agendas da Conta
                    </legend>
                    <p className="form-hint mb-4">Defina quais agendas restricted este usuário poderá ver e gerenciar na recepção.</p>

                    <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                        {agendasList.map(agenda => {
                            const config = agendasAcesso[agenda._id] || { visivel: false, editor: false };
                            const isSuaPropriaAgenda = extractId(agenda.entidadeId) === targetUserId;

                            return (
                                <div key={agenda._id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 border border-gray-200 rounded-lg bg-gray-50/50">
                                    <div>
                                        <p className="font-medium text-sm text-gray-800 flex items-center gap-2">
                                            {agenda.nomeExibicao}
                                            {isSuaPropriaAgenda && <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-[10px]">Sua Agenda</span>}
                                        </p>
                                        <p className="text-xs text-gray-500">Visibilidade Base: {agenda.permissoes?.visibilidade.replace(/_/g, ' ')}</p>
                                    </div>
                                    
                                    {!isSuaPropriaAgenda && (
                                        <div className="flex gap-4 mt-2 sm:mt-0">
                                            <label className="checkbox-label text-sm text-gray-700 cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    className="form-checkbox"
                                                    checked={config.visivel}
                                                    onChange={() => handleAgendaToggle(agenda._id, 'visivel')}
                                                    disabled={!localIsEditing}
                                                />
                                                <span className="checkbox-label-text">Pode Visualizar</span>
                                            </label>
                                            
                                            <label className="checkbox-label text-sm text-gray-700 cursor-pointer">
                                                <input 
                                                    type="checkbox" 
                                                    className="form-checkbox"
                                                    checked={config.editor}
                                                    onChange={() => handleAgendaToggle(agenda._id, 'editor')}
                                                    disabled={!localIsEditing}
                                                />
                                                <span className="checkbox-label-text">Pode Editar/Gerenciar</span>
                                            </label>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </fieldset>
            )}
        </div>
    );
};

export default PermissoesUsuarioTab;